import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import { ArrowLeft, TrendingUp, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const CaseStudyEcommerce = () => {
  useEffect(() => {
    document.title = "E-Commerce Platform Case Study - 320% Traffic Growth | NQ Marketing";
  }, []);

  return (
    <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 pt-32 pb-20">
          <Link to="/#case-studies">
            <Button variant="ghost" className="mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Button>
          </Link>

          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
                Website Marketing
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                E-Commerce Platform Achieves 320% Traffic Growth
              </h1>
              <div className="flex flex-wrap gap-6 text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>6-month project</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>Fashion Tech Industry</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>320% Growth</span>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2>The Challenge</h2>
              <p>
                A rapidly growing e-commerce platform in the fashion tech industry was struggling with low organic visibility and high customer acquisition costs. Despite having quality products, they were unable to compete with established players in search rankings, resulting in heavy dependence on paid advertising.
              </p>

              <h2>Our Approach</h2>
              <p>
                We implemented a comprehensive website marketing strategy focusing on three key pillars:
              </p>
              
              <h3>1. Technical SEO Optimization</h3>
              <ul>
                <li>Conducted comprehensive site audit identifying 87 technical issues</li>
                <li>Improved site speed by 65% through image optimization and code minification</li>
                <li>Implemented structured data markup for product pages</li>
                <li>Fixed crawl errors and optimized site architecture</li>
              </ul>

              <h3>2. Content Marketing Strategy</h3>
              <ul>
                <li>Developed keyword-targeted blog content focused on fashion trends and styling tips</li>
                <li>Created comprehensive product guides and comparison pages</li>
                <li>Optimized existing product descriptions for search intent</li>
                <li>Built internal linking structure to boost page authority</li>
              </ul>

              <h3>3. Conversion Rate Optimization</h3>
              <ul>
                <li>Redesigned product pages with improved UX and clear CTAs</li>
                <li>Implemented A/B testing program for landing pages</li>
                <li>Added trust signals including reviews and security badges</li>
                <li>Optimized checkout flow reducing cart abandonment</li>
              </ul>

              <h2>The Results</h2>
              <div className="grid md:grid-cols-3 gap-6 my-8 not-prose">
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">320%</div>
                  <div className="text-muted-foreground">Organic Traffic Increase</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">185%</div>
                  <div className="text-muted-foreground">Conversion Rate Improvement</div>
                </div>
                <div className="bg-muted/10 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">$2.4M</div>
                  <div className="text-muted-foreground">Additional Revenue</div>
                </div>
              </div>

              <h2>Key Takeaways</h2>
              <p>
                This case study demonstrates the power of a holistic website marketing approach. By combining technical excellence with strategic content creation and conversion optimization, we were able to transform the client's digital presence and significantly reduce their reliance on paid advertising.
              </p>
              <p>
                The success of this project highlights the importance of:
              </p>
              <ul>
                <li>Data-driven decision making and continuous testing</li>
                <li>Balancing technical optimization with user experience</li>
                <li>Creating valuable content that serves both users and search engines</li>
                <li>Taking a long-term approach to sustainable growth</li>
              </ul>

              <div className="bg-primary/5 border-l-4 border-primary p-6 my-8">
                <p className="text-lg italic mb-2">
                  "NQ Marketing transformed our online presence. Their strategic approach to SEO and content marketing has been instrumental in our growth. We've seen consistent month-over-month improvements and have significantly reduced our customer acquisition costs."
                </p>
                <p className="font-semibold">— Marketing Director, Fashion Tech E-Commerce</p>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t">
              <h3 className="text-2xl font-bold mb-4">Ready to achieve similar results?</h3>
              <p className="text-muted-foreground mb-6">
                Let's discuss how we can help grow your business with our proven website marketing strategies.
              </p>
              <ScheduleConsultationDialog
                buttonText="Get Started"
                buttonSize="lg"
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
  );
};

export default CaseStudyEcommerce;
